import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  connectAuthPost, getConnectToken, getConnectUser,
  clearConnectSession, normalizeUser, normalizePost
} from './connectApi';
import PostComments from './PostComments';
import NotificationsBell from './NotificationsBell';
import './styles/ConnectProfile.css';
import './styles/ProfileLayout.css';

/**
 * Full-page view of another member's profile.
 * Route: /connect/user/:id
 */
export default function ConnectUserProfile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [viewer, setViewer] = useState(null);
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [activeTab, setActiveTab] = useState('posts');
  const [openCommentsPostId, setOpenCommentsPostId] = useState(null);

  const formatTimeAgo = (timestamp) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  useEffect(() => {
    if (!getConnectToken()) {
      navigate('/connect');
      return;
    }
    setViewer(getConnectUser());

    const userId = parseInt(id, 10);
    if (!userId) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }

    // Own profile lives on its own page
    const own = getConnectUser();
    if (own && own.id === userId) {
      navigate('/connect/profile');
      return;
    }

    const load = async () => {
      try {
        const result = await connectAuthPost('connect/profile/get', { user_id: userId });
        if (!result.success) {
          setNotFound(true);
          return;
        }
        setProfile(normalizeUser(result.data.user));

        const postsResult = await connectAuthPost('connect/posts/list', { user_id: userId });
        if (postsResult.success) {
          setPosts(postsResult.data.posts.map(normalizePost));
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  const handleFollow = async () => {
    try {
      const result = await connectAuthPost('connect/users/follow', { user_id: profile.id });
      if (result.success) {
        setProfile(prev => ({
          ...prev,
          isFollowing: result.data.is_following,
          followers: result.data.followers
        }));
      }
    } catch (err) {
      console.error('Failed to toggle follow:', err);
    }
  };

  const handleToggleLike = async (postId) => {
    try {
      const result = await connectAuthPost('connect/posts/like', { post_id: postId });
      if (result.success) {
        setPosts(prev => prev.map(p =>
          p.id === postId
            ? { ...p, likes: result.data.likes, likedByViewer: result.data.liked }
            : p
        ));
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await connectAuthPost('connect/auth/logout');
    } catch (err) {
      // Best-effort — clear locally regardless
    }
    clearConnectSession();
    navigate('/connect');
  };

  if (!viewer) return null;

  return (
    <div className="connect-profile-page">
      {/* Header */}
      <header className="connect-profile-header">
        <Link to="/" className="connect-profile-logo">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="OutCaribbean" />
        </Link>
        <nav className="connect-profile-nav">
          <Link to="/connect/profile" className="connect-nav-link">Profile</Link>
          <Link to="/connect/discover" className="connect-nav-link active">Discover</Link>
          <Link to="/connect/messages" className="connect-nav-link">Messages</Link>
        </nav>
        <div className="connect-header-actions">
          <NotificationsBell />
          <button className="connect-logout-btn" onClick={handleLogout}>
            Log Out
          </button>
          <button className="connect-mobile-nav-toggle" onClick={() => setShowMobileNav(true)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      {/* Mobile Navigation */}
      {showMobileNav && (
        <>
          <div className="connect-mobile-nav-overlay active" onClick={() => setShowMobileNav(false)}></div>
          <div className="connect-mobile-nav-menu active">
            <div className="connect-mobile-nav-header">
              <h3>Menu</h3>
              <button className="connect-mobile-nav-close" onClick={() => setShowMobileNav(false)}>×</button>
            </div>
            <div className="connect-mobile-nav-links">
              <Link to="/connect/profile" className="connect-nav-link" onClick={() => setShowMobileNav(false)}>Profile</Link>
              <Link to="/connect/discover" className="connect-nav-link active" onClick={() => setShowMobileNav(false)}>Discover</Link>
              <Link to="/connect/messages" className="connect-nav-link" onClick={() => setShowMobileNav(false)}>Messages</Link>
            </div>
            <div className="connect-mobile-nav-footer">
              <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
            </div>
          </div>
        </>
      )}

      <main className="cpx-main">
        <button className="cpx-back" onClick={() => navigate('/connect/discover')}>
          ← Back to Discover
        </button>

        {isLoading ? (
          <div className="cpx-state">
            <h3>Loading profile...</h3>
          </div>
        ) : notFound ? (
          <div className="cpx-state">
            <h3>Profile not found</h3>
            <p>This profile doesn't exist or is private.</p>
          </div>
        ) : (
          <>
            {/* Cover */}
            <div className="cpx-cover">
              {profile.coverPhoto ? (
                <img src={profile.coverPhoto} alt="Cover" />
              ) : (
                <div className="cpx-cover-placeholder"></div>
              )}
            </div>

            <div className="cpx-layout">
              {/* Identity card */}
              <aside className="cpx-card">
                <div className="cpx-avatar">
                  {profile.profilePicture ? (
                    <img src={profile.profilePicture} alt={profile.fullName} />
                  ) : (
                    <div className="cpx-avatar-placeholder">
                      {profile.fullName ? profile.fullName.charAt(0).toUpperCase() : '?'}
                    </div>
                  )}
                </div>

                <h1 className="cpx-name">{profile.fullName}</h1>
                {profile.pronouns && (
                  <span className="cpx-pronouns">{profile.pronouns}</span>
                )}
                {profile.isLocalGuide && (
                  <span className="cpx-guide-badge">🌴 Local Guide</span>
                )}

                {profile.currentLocation && (
                  <p className="cpx-location">📍 {profile.currentLocation}</p>
                )}

                <div className="cpx-stats">
                  <div>
                    <strong>{profile.followers}</strong>
                    <span>Followers</span>
                  </div>
                  <div>
                    <strong>{profile.following}</strong>
                    <span>Following</span>
                  </div>
                  <div>
                    <strong>{posts.length}</strong>
                    <span>Posts</span>
                  </div>
                </div>

                {(profile.genderIdentity || profile.sexualOrientation || profile.travelStyle) && (
                  <>
                    <div className="cpx-label">Identity</div>
                    <div className="cpx-tags">
                      {profile.genderIdentity && (
                        <span className="cpx-tag">{profile.genderIdentity}</span>
                      )}
                      {profile.sexualOrientation && (
                        <span className="cpx-tag">{profile.sexualOrientation}</span>
                      )}
                      {profile.travelStyle && (
                        <span className="cpx-tag travel">{profile.travelStyle}</span>
                      )}
                    </div>
                  </>
                )}

                {profile.bio && (
                  <>
                    <div className="cpx-label">About</div>
                    <p className="cpx-bio">{profile.bio}</p>
                  </>
                )}

                {profile.favoriteDestinations.length > 0 && (
                  <>
                    <div className="cpx-label">Favorite destinations</div>
                    <div className="cpx-stamps">
                      {profile.favoriteDestinations.map(dest => (
                        <span key={dest} className="cpx-stamp">{dest}</span>
                      ))}
                    </div>
                  </>
                )}

                <div className="cpx-actions">
                  <button
                    className={`cpx-btn primary ${profile.isFollowing ? 'following' : ''}`}
                    onClick={handleFollow}
                  >
                    {profile.isFollowing ? '✓ Following' : '+ Follow'}
                  </button>
                  <Link to={`/connect/messages?user=${profile.id}`} className="cpx-btn ghost">
                    Message
                  </Link>
                </div>
              </aside>

              {/* Feed */}
              <section className="cpx-feed">
                <div className="cpx-tabs">
                  <button
                    className={`cpx-tab ${activeTab === 'posts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('posts')}
                  >
                    Posts ({posts.length})
                  </button>
                  <button
                    className={`cpx-tab ${activeTab === 'photos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('photos')}
                  >
                    Photos
                  </button>
                </div>

                {activeTab === 'posts' && (
                  <div className="connect-posts-list">
                    {posts.length === 0 && (
                      <div className="cpx-state">
                        <h3>No posts yet</h3>
                        <p>{profile.fullName} hasn't shared anything yet</p>
                      </div>
                    )}
                    {posts.map(post => (
                      <div key={post.id} className="connect-post-card">
                        <div className="connect-post-header">
                          <div className="connect-post-avatar">
                            {profile.profilePicture ? (
                              <img src={profile.profilePicture} alt={profile.fullName} />
                            ) : (
                              <div className="connect-post-avatar-placeholder">
                                {profile.fullName ? profile.fullName.charAt(0).toUpperCase() : '?'}
                              </div>
                            )}
                          </div>
                          <div className="connect-post-meta">
                            <span className="connect-post-author">{profile.fullName}</span>
                            <span className="connect-post-time">
                              {formatTimeAgo(post.timestamp)}
                              {post.location && ` · 📍 ${post.location}`}
                            </span>
                          </div>
                        </div>
                        <p className="connect-post-content">{post.content}</p>
                        {post.image && (
                          <div className="connect-post-image">
                            <img src={post.image} alt="Post" />
                          </div>
                        )}
                        <div className="connect-post-actions">
                          <button
                            className={`connect-post-action ${post.likedByViewer ? 'liked' : ''}`}
                            onClick={() => handleToggleLike(post.id)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill={post.likedByViewer ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                            </svg>
                            <span>{post.likes}</span>
                          </button>
                          <button
                            className={`connect-post-action ${openCommentsPostId === post.id ? 'liked' : ''}`}
                            onClick={() => setOpenCommentsPostId(prev => prev === post.id ? null : post.id)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                            </svg>
                            <span>{post.comments}</span>
                          </button>
                        </div>
                        {openCommentsPostId === post.id && (
                          <PostComments
                            postId={post.id}
                            onCountChange={(pid, count) =>
                              setPosts(prev => prev.map(p => p.id === pid ? { ...p, comments: count } : p))
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'photos' && (
                  <div className="connect-photos-grid">
                    {posts.filter(p => p.image).length === 0 && (
                      <div className="cpx-state">
                        <h3>No photos yet</h3>
                      </div>
                    )}
                    {posts.filter(p => p.image).map(post => (
                      <div key={post.id} className="connect-photo-item">
                        <img src={post.image} alt="From a post" />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
